import { useState, useEffect } from 'react';
import { 
  Box, Grid, Paper, Typography, Card, CardContent, 
  CircularProgress, Divider
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import BookIcon from '@mui/icons-material/Book';
import CategoryIcon from '@mui/icons-material/Category';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PeopleIcon from '@mui/icons-material/People';
import { booksAPI, categoriesAPI, collectionsAPI } from '../services/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalBooks: 0,
    totalCategories: 0,
    totalCollections: 0,
    totalUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Content Distribution',
      },
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch data from APIs
        const [booksResponse, categoriesResponse, collectionsResponse] = await Promise.all([
          booksAPI.getAllBooks(),
          categoriesAPI.getAllCategories(),
          collectionsAPI.getAllCollections()
        ]);
        
        // Update stats
        setStats({
          totalBooks: booksResponse.data?.length || 0,
          totalCategories: categoriesResponse.data?.categories?.length || 0,
          totalCollections: collectionsResponse.data?.collections?.length || 0,
          totalUsers: 150 // Placeholder, not available in API
        });
        
        // Prepare chart data
        // This is just sample data as the actual data structure depends on the API response
        const categories = categoriesResponse.data?.categories || [];
        const categoryNames = categories.slice(0, 5).map(cat => cat.name);
        const bookCounts = categories.slice(0, 5).map(cat => cat.booksCount || Math.floor(Math.random() * 10) + 1);
        
        setChartData({
          labels: categoryNames,
          datasets: [
            {
              label: 'Books per Category',
              data: bookCounts,
              backgroundColor: 'rgba(53, 162, 235, 0.5)',
            },
          ],
        });
        
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const statCards = [
    { title: 'Total Books', value: stats.totalBooks, icon: <BookIcon sx={{ fontSize: 40, color: '#2196f3' }} /> },
    { title: 'Total Categories', value: stats.totalCategories, icon: <CategoryIcon sx={{ fontSize: 40, color: '#ff9800' }} /> },
    { title: 'Total Collections', value: stats.totalCollections, icon: <LibraryBooksIcon sx={{ fontSize: 40, color: '#4caf50' }} /> },
    { title: 'Total Users', value: stats.totalUsers, icon: <PeopleIcon sx={{ fontSize: 40, color: '#9c27b0' }} /> }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
        Dashboard
      </Typography>
      
      <Grid container spacing={3}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h5" component="div" fontWeight="bold">
                      {stat.value}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                  <Box>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Content Overview
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 300 }}>
              <Bar options={options} data={chartData} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Recent Activity
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ bgcolor: '#e3f2fd', p: 1, borderRadius: 1 }}>
                  <BookIcon color="primary" />
                </Box>
                <Box>
                  <Typography variant="body2">New book added: The Art of Focus</Typography>
                  <Typography variant="caption" color="text.secondary">2 hours ago</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ bgcolor: '#fff8e1', p: 1, borderRadius: 1 }}>
                  <CategoryIcon sx={{ color: '#ff9800' }} />
                </Box>
                <Box>
                  <Typography variant="body2">Category updated: Psychology</Typography>
                  <Typography variant="caption" color="text.secondary">5 hours ago</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ bgcolor: '#e8f5e9', p: 1, borderRadius: 1 }}>
                  <LibraryBooksIcon sx={{ color: '#4caf50' }} />
                </Box>
                <Box>
                  <Typography variant="body2">New collection: Summer Reads</Typography>
                  <Typography variant="caption" color="text.secondary">Yesterday</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
